import {
  ComponentProps,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { ButtonWithIcon } from 'src/components/Buttons';
import { BulkActionItem } from 'src/components/Listings/InventoryActionDropdown/components/types';
import { useAppContext } from 'src/contexts/AppContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { useCommandMenuContext } from 'src/contexts/CommandMenuContext/CommandMenuContext';
import { PosDropdownItem } from 'src/core/POS/PosDropdown';
import { RotatingWrapper } from 'src/core/ui/AnimatingWrapper';
import { useEventCallback } from 'src/hooks/useEventCallback';
import { DownloadIcon, IconsFill, ProcessingIcon } from 'src/svgs/Viagogo';
import { usePurchasesTableColumnIdOrder } from 'src/tables/PurchaseTicketTable/useGetPurchasesTableColumns';
import { ContentId } from 'src/utils/constants/contentId';
import { downloadFileFromBlob } from 'src/utils/fileUtils';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  CommandOptionType,
  PurchaseClient,
  PurchaseOrderQuery,
  PurchaseViewMode,
} from 'src/WebApiController';

export const useExportPurchasesAction = ({
  filterQuery,
  textContentId,
  iconOnlyMode,
  textOnly,
  variant,
  isEventView,
  ...props
}: {
  filterQuery: PurchaseOrderQuery;
  textContentId?: ContentId;
  iconOnlyMode?: boolean;
  textOnly?: boolean;
  variant?: string;
  isEventView: boolean;
} & Omit<ComponentProps<typeof ButtonWithIcon>, 'icon' | 'onClick'>) => {
  const { eventsTransformed } = useCatalogDataContext();
  const { activeAccountWebClientConfig } = useAppContext();
  const { registerCommand, unregisterCommand } = useCommandMenuContext();

  const { showErrorDialog } = useClientLogContext();
  const [isLoading, setIsLoading] = useState(false);
  const isButtonDisabled = isLoading || eventsTransformed == null;
  const { purchaseColumnIdOrder } = usePurchasesTableColumnIdOrder({
    viewMode: isEventView
      ? PurchaseViewMode.EventTileView
      : PurchaseViewMode.TileView,
  });

  const onExportButtonClicked = useCallback(async () => {
    setIsLoading(true);
    await tryInvokeApi(
      async () => {
        const result = await new PurchaseClient(
          activeAccountWebClientConfig
        ).exportPurchases(
          {
            query: filterQuery,
            exportColumns: purchaseColumnIdOrder,
          },
          isEventView
        );

        if (result.data) {
          downloadFileFromBlob(result.fileName ?? 'purchase.csv', result.data);
        }
      },
      (error) => {
        showErrorDialog('PurchaseClient.exportPurchases', error, {
          trackErrorData: { filterQuery, purchaseColumnIdOrder, isEventView },
        });
      },
      () => setIsLoading(false)
    );
  }, [activeAccountWebClientConfig, filterQuery, isEventView, showErrorDialog]);

  const iconFill = useMemo(
    () =>
      props.disabled
        ? IconsFill.textDisabled
        : iconOnlyMode || (variant ?? 'text') !== 'regular'
          ? IconsFill.textBrand
          : IconsFill.textInverted,
    [iconOnlyMode, props.disabled, variant]
  );

  const openDialogStableRef = useEventCallback(() => {
    isButtonDisabled ? undefined : onExportButtonClicked();
  });

  useEffect(() => {
    registerCommand({
      command: {
        id: CommandOptionType.PurchasesExportPurchases,
        onSelect: openDialogStableRef,
      },
    });

    return () => {
      unregisterCommand({
        commandId: CommandOptionType.PurchasesExportPurchases,
      });
    };
  }, [openDialogStableRef, registerCommand, unregisterCommand]);

  const actionItems = useMemo<BulkActionItem[]>(() => {
    return [
      {
        title: ContentId.Export,
        onClick: onExportButtonClicked,
        disabled: isLoading || props.disabled,
      },
    ];
  }, [isLoading, onExportButtonClicked, props.disabled]);

  return {
    dropdown: (
      <PosDropdownItem key="ExportPurchase">
        <ButtonWithIcon
          {...props}
          onClick={isButtonDisabled ? undefined : onExportButtonClicked}
          disabled={isLoading || props.disabled}
          icon={
            !textOnly ? (
              isLoading ? (
                <RotatingWrapper>
                  <ProcessingIcon fill={iconFill} />
                </RotatingWrapper>
              ) : (
                <DownloadIcon withHoverEffect fill={iconFill} />
              )
            ) : null
          }
          iconOnlyMode={iconOnlyMode}
          variant={variant ?? 'text'}
          textContentId={textContentId || ContentId.Export}
        />
      </PosDropdownItem>
    ),
    actionItems,
  };
};
